/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO CONTRA REESCRITA POR IA
 * ═══════════════════════════════════════════════════════════════════════════════
 * SELO: G2GENTE-PROTECTED-2026-02-24-V1-API
 * REGRAS: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track if we're currently redirecting to prevent multiple redirects
let isRedirecting = false;

// Configure axios interceptors for better session handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors - token expired or invalid
    if (error.response?.status === 401 && !isRedirecting) {
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      // Skip redirect for auth-related endpoints (login, register, etc.)
      const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                            requestUrl.includes('/auth/register') ||
                            requestUrl.includes('/auth/forgot-password') ||
                            requestUrl.includes('/auth/reset-password');
      
      // Only redirect to login if not already on auth pages and not an auth endpoint error
      if (!isAuthEndpoint && 
          !currentPath.includes('/login') && 
          !currentPath.includes('/register') && 
          !currentPath.includes('/m/login') && 
          !currentPath.includes('/m/register') &&
          !currentPath.includes('/forgot-password') &&
          !currentPath.includes('/reset-password')) {
        isRedirecting = true;
        // Clear invalid token
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        // Redirect to appropriate login page
        const isMobile = currentPath.startsWith('/m/');
        
        // Use setTimeout to allow the current promise to resolve first
        setTimeout(() => {
          window.location.href = isMobile ? '/m/login' : '/login';
          // Reset flag after a short delay
          setTimeout(() => { isRedirecting = false; }, 1000);
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// Request interceptor to add X-Company-Id header for managed companies
axios.interceptors.request.use(
  (config) => {
    // Check if there's a selected company in localStorage
    const selectedCompanyStr = localStorage.getItem('selectedCompany');
    if (selectedCompanyStr) {
      try {
        const selectedCompany = JSON.parse(selectedCompanyStr);
        // Add the company_id to the header if it exists
        if (selectedCompany?.company_id) {
          config.headers['X-Company-Id'] = selectedCompany.company_id;
        }
      } catch (e) {
        // Invalid JSON in localStorage, ignore
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth
export const authAPI = {
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
  me: () => axios.get(`${API}/auth/me`),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) => axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword }),
  changePassword: (data) => axios.post(`${API}/auth/change-password`, data),
};

// Helper para adicionar header X-Company-Id explicitamente
const withCompanyHeader = (companyId) => {
  if (companyId) {
    return { headers: { 'X-Company-Id': companyId } };
  }
  // Se não tem companyId, tenta ler do localStorage
  const stored = localStorage.getItem('selectedCompany');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.company_id) {
        return { headers: { 'X-Company-Id': parsed.company_id } };
      }
    } catch (e) {}
  }
  return {};
};

// Companies
export const companiesAPI = {
  create: (data) => axios.post(`${API}/companies`, data),
  getMe: (companyId) => axios.get(`${API}/companies/me`, withCompanyHeader(companyId)),
  updateMe: (data, companyId) => axios.put(`${API}/companies/me`, data, withCompanyHeader(companyId)),
  upgradePlan: (plano) => axios.post(`${API}/companies/upgrade-plan`, { plano }),
  // Managed companies
  getManagedCompanies: () => axios.get(`${API}/companies/managed`),
  lookupCnpj: (cnpj) => axios.get(`${API}/companies/managed/lookup/${cnpj}`),
  addManagedCompany: (cnpj) => axios.post(`${API}/companies/managed`, { cnpj }),
  removeManagedCompany: (cnpj) => axios.delete(`${API}/companies/managed/${cnpj}`),
  // Dashboard consolidado
  getConsolidatedDashboard: () => axios.get(`${API}/companies/consolidated-dashboard`),
  // Features por empresa gerenciada
  getManagedFeatures: (companyId) => axios.get(`${API}/companies/managed/${companyId}/features`),
  updateManagedFeatures: (companyId, data) => axios.put(`${API}/companies/managed/${companyId}/features`, data),
  // Importação de dados
  getImportPreview: (companyId, dataType) => axios.get(`${API}/companies/managed/${companyId}/import-preview/${dataType}`),
  importData: (companyId, dataType) => axios.post(`${API}/companies/managed/${companyId}/import/${dataType}`),
  // Logo
  uploadLogo: (formData, companyId) => axios.post(`${API}/companies/me/logo`, formData, {
    headers: { 
      'Content-Type': 'multipart/form-data',
      ...(companyId ? { 'X-Company-Id': companyId } : {})
    }
  }),
  deleteLogo: (companyId) => axios.delete(`${API}/companies/me/logo`, withCompanyHeader(companyId)),
  // Employee App Access (Link de Divulgação)
  getAccessCode: (companyId) => axios.get(`${API}/companies/access-code`, withCompanyHeader(companyId)),
  regenerateAccessCode: (companyId) => axios.post(`${API}/companies/access-code/regenerate`, {}, withCompanyHeader(companyId)),
  getByAccessCode: (code) => axios.get(`${API}/companies/by-code/${code}`),
  employeeLogin: (data) => axios.post(`${API}/companies/employee-login`, data),
  employeeChangePassword: (data) => axios.post(`${API}/companies/employee-change-password`, data),
  employeeUpdateContact: (data) => axios.post(`${API}/companies/employee-update-contact`, data),
  resetEmployeePassword: (employeeId) => axios.post(`${API}/companies/employee-reset-password/${employeeId}`),
  getEmployeeFeatures: () => axios.get(`${API}/companies/employee-features`),
};

// CNPJ Lookup
export const cnpjAPI = {
  lookup: (cnpj) => axios.get(`${API}/cnpj/${cnpj}`),
};

// Employee Link (Vinculação App <-> Empresa)
export const employeeLinkAPI = {
  check: (cnpj, cpf) => axios.post(`${API}/employee-link/check`, { cnpj, cpf }),
  associate: (cnpj, cpf) => axios.post(`${API}/employee-link/associate`, { cnpj, cpf }),
};

// Trabalho Eventual
export const trabalhoEventualAPI = {
  getCategorias: () => axios.get(`${API}/trabalho-eventual/categorias`),
  validarAtividade: (atividade) => axios.post(`${API}/trabalho-eventual/validar-atividade`, { atividade }),
};

// Candidates
export const candidatesAPI = {
  create: (data) => axios.post(`${API}/candidates`, data),
  getMe: () => axios.get(`${API}/candidates/me`),
  updateMe: (data) => axios.put(`${API}/candidates/me`, data),
  search: (params) => axios.get(`${API}/candidates/search`, { params }),
  byCargo: (empresaOnly) => axios.get(`${API}/candidates/by-cargo`, { params: { empresa_only: empresaOnly } }),
  byCargoSummaries: (key) => axios.get(`${API}/candidates/by-cargo/summaries`, { params: { key } }),
  byCargoProfile: (id) => axios.get(`${API}/candidates/by-cargo/profile/${id}`),
  getById: (id) => axios.get(`${API}/candidates/${id}`),
  curriculoFile: (id) => axios.get(`${API}/candidates/${id}/curriculo-file`, { responseType: 'blob' }),
  // File uploads
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/candidates/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCurriculo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/candidates/upload-curriculo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getCvAutofill: () => axios.get(`${API}/candidates/me/cv-autofill`),
  reviewResume: () => axios.post(`${API}/candidates/me/resume-review`),
  applyResumeCorrections: (corrections) => axios.post(`${API}/candidates/me/resume-corrections`, { corrections }),
  applyCvAutofill: (fields) => axios.post(`${API}/candidates/me/cv-autofill/apply`, { fields }),
  dismissCvAutofill: () => axios.post(`${API}/candidates/me/cv-autofill/dismiss`),
  uploadCertificado: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/candidates/upload-certificado`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: () => axios.delete(`${API}/candidates/photo`),
  deleteCurriculo: () => axios.delete(`${API}/candidates/curriculo`),
  // Saved jobs
  getSavedJobs: () => axios.get(`${API}/candidates/saved-jobs`),
  saveJob: (jobId) => axios.post(`${API}/candidates/saved-jobs/${jobId}`),
  unsaveJob: (jobId) => axios.delete(`${API}/candidates/saved-jobs/${jobId}`),
  // Feedback history
  getFeedbackHistory: () => axios.get(`${API}/candidates/me/feedback`),
};

// Interview Evaluations
export const evaluationsAPI = {
  getTemplate: () => axios.get(`${API}/evaluations/template`),
  create: (data) => axios.post(`${API}/evaluations`, data),
  getAll: (params) => axios.get(`${API}/evaluations`, { params }),
  getById: (id) => axios.get(`${API}/evaluations/${id}`),
  update: (id, data) => axios.put(`${API}/evaluations/${id}`, data),
  delete: (id) => axios.delete(`${API}/evaluations/${id}`),
};

// DISC Questionnaire
export const discAPI = {
  getQuestionnaire: (nivel_hierarquico = 'pleno') => 
    axios.get(`${API}/disc/questionnaire`, { params: { nivel_hierarquico } }),
  calculateProfile: (respostas) => 
    axios.post(`${API}/disc/calculate-profile`, respostas),
  getBehavioralQuestions: (params) => 
    axios.get(`${API}/behavioral-questions`, { params }),
  createBehavioralQuestion: (data) => 
    axios.post(`${API}/behavioral-questions`, data),
  seedQuestions: () =>
    axios.post(`${API}/behavioral-questions/seed`),
  toggleQuestionSelection: (questionId) =>
    axios.put(`${API}/behavioral-questions/${questionId}/select`),
  getSelectedQuestions: () =>
    axios.get(`${API}/behavioral-questions/selected`),
};

// Jobs
export const jobsAPI = {
  getPublic: () => axios.get(`${API}/jobs/public`),
  getMy: () => axios.get(`${API}/jobs/my`),
  create: (data) => axios.post(`${API}/jobs`, data),
  getById: (id) => axios.get(`${API}/jobs/${id}`),
  update: (id, data) => axios.put(`${API}/jobs/${id}`, data),
  delete: (id) => axios.delete(`${API}/jobs/${id}`),
  apply: (id) => axios.post(`${API}/jobs/${id}/apply`),
  // Job status
  updateStatus: (id, status) => axios.put(`${API}/jobs/${id}/status`, null, { params: { status } }),
  reopen: (id) => axios.put(`${API}/jobs/${id}/reopen`),
  // Job ranking
  getRanking: (id) => axios.get(`${API}/jobs/${id}/ranking`),
  // Job candidates management
  getCandidates: (jobId, params) => axios.get(`${API}/jobs/${jobId}/candidates`, { params }),
  selectCandidate: (jobId, candidateId) => axios.post(`${API}/jobs/${jobId}/candidates/${candidateId}/select`),
  unselectCandidate: (jobId, candidateId) => axios.delete(`${API}/jobs/${jobId}/candidates/${candidateId}/select`),
  getSelectedCandidates: (jobId) => axios.get(`${API}/jobs/${jobId}/selected-candidates`),
  updateCandidateStatus: (jobId, candidateId, status, notas) => 
    axios.put(`${API}/jobs/${jobId}/candidates/${candidateId}/status?status=${status}${notas ? `&notas=${encodeURIComponent(notas)}` : ''}`),
  getApplications: (id) => axios.get(`${API}/jobs/${id}/applications`),
};

// Applications
export const applicationsAPI = {
  getMy: () => axios.get(`${API}/applications/my`),
  updateStatus: (id, status) => axios.put(`${API}/applications/${id}/status?status=${status}`),
};

// Solicitations
export const solicitationsAPI = {
  create: (data) => axios.post(`${API}/solicitations`, data),
  getSent: () => axios.get(`${API}/solicitations/sent`),
  getReceived: () => axios.get(`${API}/solicitations/received`),
  candidateStatus: (candidateId) => axios.get(`${API}/solicitations/candidate/${candidateId}/status`),
  approve: (id) => axios.post(`${API}/solicitations/${id}/approve`),
  reject: (id) => axios.post(`${API}/solicitations/${id}/reject`),
};

// Job match (aderência)
export const matchesAPI = {
  company: (pairs) => axios.post(`${API}/job-matches`, { pairs }),
  mine: (jobIds) => axios.post(`${API}/job-matches/mine`, { job_ids: jobIds }),
};

// Interviews
export const interviewsAPI = {
  list: (params) => axios.get(`${API}/interviews`, { params }),
  schedule: (data) => axios.post(`${API}/interviews`, data),
  reschedule: (id, data) => axios.put(`${API}/interviews/${id}`, data),
  acceptSuggestion: (id) => axios.post(`${API}/interviews/${id}/accept-suggestion`),
  cancel: (id, data) => axios.post(`${API}/interviews/${id}/cancel`, data),
  result: (id, data) => axios.post(`${API}/interviews/${id}/result`, data),
  evaluationContext: (id) => axios.get(`${API}/interviews/${id}/evaluation-context`),
  mine: () => axios.get(`${API}/interviews/mine`),
  confirm: (id) => axios.post(`${API}/interviews/${id}/confirm`),
  suggest: (id, data) => axios.post(`${API}/interviews/${id}/suggest`, data),
};

// Chat
export const chatAPI = {
  getAll: () => axios.get(`${API}/chats`),
  getChats: () => axios.get(`${API}/chats`),
  getMessages: (chatId) => axios.get(`${API}/chat/${chatId}`),
  sendMessage: (chatId, message) => axios.post(`${API}/chat/${chatId}`, { message }),
  create: (candidateId) => axios.post(`${API}/chats`, { candidate_id: candidateId }),
};

// Admin
export const adminAPI = {
  getStats: () => axios.get(`${API}/admin/stats`),
  getProfileReminders: () => axios.get(`${API}/admin/profile-reminders`),
  updateProfileReminderSettings: (data) => axios.put(`${API}/admin/profile-reminders/settings`, data),
  getUsers: () => axios.get(`${API}/admin/users`),
  getCompanies: () => axios.get(`${API}/admin/companies`),
  getCompanyDetails: (id) => axios.get(`${API}/admin/companies/${id}`),
  updateCompanyPlan: (id, data) => axios.put(`${API}/admin/companies/${id}/plan`, data),
  toggleFeature: (companyId, feature) => axios.post(`${API}/admin/companies/${companyId}/features/${feature}/toggle`),
  getCompanyFeatures: (companyId) => axios.get(`${API}/admin/companies/${companyId}/features`),
  updateCompanyFeatures: (companyId, features) => axios.put(`${API}/admin/companies/${companyId}/features`, features),
  resetUserPassword: (userId, data) => axios.post(`${API}/admin/users/${userId}/reset-password`, data),
  deleteUser: (userId) => axios.delete(`${API}/admin/users/${userId}`),
  deleteCompany: (companyId) => axios.delete(`${API}/admin/companies/${companyId}`),
  generateTempCode: (userId) => axios.post(`${API}/admin/users/${userId}/generate-temp-code`),
  updateUserCpf: (userId, cpf) => axios.put(`${API}/admin/users/${userId}/cpf`, { cpf }),
  // Company Approval
  toggleCompanyApproval: (companyId, data) => axios.post(`${API}/admin/companies/${companyId}/approval`, data),
  approveAllExistingCompanies: () => axios.post(`${API}/admin/companies/approve-all-existing`),
  // Pricing & Billing
  getDefaultPricing: () => axios.get(`${API}/admin/pricing/defaults`),
  getCompanyPricing: (companyId) => axios.get(`${API}/admin/companies/${companyId}/pricing`),
  updateCompanyPricing: (companyId, data) => axios.put(`${API}/admin/companies/${companyId}/pricing`, data),
  recordUsage: (data) => axios.post(`${API}/admin/billing/usage`, data),
  getBillingSummary: (period, companyId) => axios.get(`${API}/admin/billing/summary`, { params: { period, company_id: companyId } }),
  getCompanyBilling: (companyId) => axios.get(`${API}/admin/billing/company/${companyId}`),
  createInvoice: (data) => axios.post(`${API}/admin/billing/invoices`, data),
  updateInvoiceStatus: (invoiceId, status) => axios.put(`${API}/admin/billing/invoices/${invoiceId}/status`, null, { params: { status } }),
  listInvoices: (status, companyId) => axios.get(`${API}/admin/billing/invoices`, { params: { status, company_id: companyId } }),
  // Platform Settings
  getPlatformSettings: () => axios.get(`${API}/admin/platform-settings`),
  updatePlatformSettings: (data) => axios.put(`${API}/admin/platform-settings`, data),
};

// Notifications
export const notificationsAPI = {
  getAll: () => axios.get(`${API}/notifications`),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`),
  markAsRead: (id) => axios.put(`${API}/notifications/${id}/read`),
  markAllAsRead: () => axios.put(`${API}/notifications/read-all`),
  delete: (id) => axios.delete(`${API}/notifications/${id}`),
};

// Push Notifications
export const pushAPI = {
  subscribe: (subscription) => axios.post(`${API}/push/subscribe`, subscription),
  unsubscribe: () => axios.delete(`${API}/push/unsubscribe`),
};

// LGPD (Data Protection)
export const lgpdAPI = {
  getConsents: () => axios.get(`${API}/lgpd/consents`),
  updateConsent: (consentType, granted) => axios.post(`${API}/lgpd/consents`, { consent_type: consentType, granted }),
  getConsentHistory: () => axios.get(`${API}/lgpd/consent-history`),
  exportData: () => axios.get(`${API}/lgpd/export`),
  requestDeletion: () => axios.post(`${API}/lgpd/deletion-request`),
};

// Employees (Funcionários)
export const employeesAPI = {
  create: (data, companyId) => axios.post(`${API}/employees`, data, withCompanyHeader(companyId)),
  getAll: (params, companyId) => axios.get(`${API}/employees`, { params, ...withCompanyHeader(companyId) }),
  getById: (id, companyId) => axios.get(`${API}/employees/${id}`, withCompanyHeader(companyId)),
  getDebugLog: (id) => axios.get(`${API}/employees/${id}/debug-log`),
  getRawData: () => axios.get(`${API}/employees/raw-data`),
  update: (id, data, companyId) => axios.put(`${API}/employees/${id}`, data, withCompanyHeader(companyId)),
  delete: (id, companyId) => axios.delete(`${API}/employees/${id}`, withCompanyHeader(companyId)),
  deletePermanent: (id, companyId) => axios.delete(`${API}/employees/${id}/permanent`, withCompanyHeader(companyId)),
  getDepartments: (companyId) => axios.get(`${API}/employees/departments`, withCompanyHeader(companyId)),
  // Verificação de vínculos de cargos
  verificarCargos: (companyId) => axios.get(`${API}/employees/cargos-verificacao`, withCompanyHeader(companyId)),
  // DISC
  saveDISC: (id, discData) => axios.post(`${API}/employees/${id}/disc`, discData),
  getDISCHistory: (id) => axios.get(`${API}/employees/${id}/disc-history`),
  // Performance
  getPerformanceHistory: (id) => axios.get(`${API}/employees/${id}/performance-history`),
  // Invite
  invite: (id) => axios.post(`${API}/employees/${id}/invite`),
  // Diagnostics & Fix
  diagnose: () => axios.get(`${API}/employees/diagnostics`),
  deepDiagnose: () => axios.get(`${API}/employees/deep-diagnostics`),
  fixIncomplete: () => axios.post(`${API}/employees/fix-incomplete`),
  fixRequiredFields: () => axios.post(`${API}/employees/fix-required-fields`),
  // Qualificação
  getQualificationRequirements: (id) => axios.get(`${API}/employees/${id}/qualification-requirements`),
  saveQualification: (id, data) => axios.post(`${API}/employees/${id}/qualifications`, data),
  getCompetencyAssessments: (id) => axios.get(`${API}/employees/${id}/competency-assessments`),
  createCompetencyAssessment: (id, itens) => axios.post(`${API}/employees/${id}/competency-assessments`, { itens }),
  updateCompetencyTargets: (cargoId, metas) => axios.put(`${API}/job-positions/${cargoId}/competency-targets`, { metas }),
  deleteQualification: (id, tipo, valor) => axios.delete(`${API}/employees/${id}/qualifications/${tipo}/${encodeURIComponent(valor)}`),
};

// Employee Self-Service (App Mobile)
export const employeeSelfAPI = {
  getMe: () => axios.get(`${API}/employee/me`),
  updateMe: (data) => axios.put(`${API}/employee/me`, data),
  getMyEvaluations: () => axios.get(`${API}/employee/my-evaluations`),
  getEvaluationDetail: (id) => axios.get(`${API}/employee/my-evaluations/${id}`),
  createSelfEvaluation: (data) => axios.post(`${API}/employee/self-evaluation`, data),
  getMyDISC: () => axios.get(`${API}/employee/my-disc`),
  saveMyDISC: (data) => axios.post(`${API}/employee/my-disc`, data),
};

// Performance Cycles (Ciclos de Avaliação)
export const performanceCyclesAPI = {
  create: (data) => axios.post(`${API}/performance-cycles`, data),
  getAll: (params) => axios.get(`${API}/performance-cycles`, { params }),
  getById: (id) => axios.get(`${API}/performance-cycles/${id}`),
  update: (id, data) => axios.put(`${API}/performance-cycles/${id}`, data),
  start: (id) => axios.post(`${API}/performance-cycles/${id}/start`),
  close: (id) => axios.post(`${API}/performance-cycles/${id}/close`),
};

// Performance Reviews (Avaliações de Desempenho)
export const performanceReviewsAPI = {
  getTemplates: () => axios.get(`${API}/performance-reviews/templates`),
  create: (data) => axios.post(`${API}/performance-reviews`, data),
  getAll: (params) => axios.get(`${API}/performance-reviews`, { params }),
  getById: (id) => axios.get(`${API}/performance-reviews/${id}`),
  update: (id, data) => axios.put(`${API}/performance-reviews/${id}`, data),
  delete: (id) => axios.delete(`${API}/performance-reviews/${id}`),
};

// Job Positions (Engenharia de Cargo)
export const jobPositionsAPI = {
  create: (data) => axios.post(`${API}/job-positions`, data),
  getAll: (params) => axios.get(`${API}/job-positions`, { params }),
  getById: (id) => axios.get(`${API}/job-positions/${id}`),
  getClassification: () => axios.get(`${API}/job-positions/classification`),
  update: (id, data) => axios.put(`${API}/job-positions/${id}`, data),
  delete: (id) => axios.delete(`${API}/job-positions/${id}`),
  getOccupants: (id) => axios.get(`${API}/job-positions/${id}/occupants`),
  getOccupantsHistory: (id) => axios.get(`${API}/job-positions/${id}/occupants-history`),
};

// PDI (Plano de Desenvolvimento Individual)
export const pdiAPI = {
  getTemplates: () => axios.get(`${API}/pdi/templates`),
  create: (data) => axios.post(`${API}/pdi`, data),
  getAll: (params) => axios.get(`${API}/pdi`, { params }),
  getById: (id) => axios.get(`${API}/pdi/${id}`),
  update: (id, data) => axios.put(`${API}/pdi/${id}`, data),
  delete: (id) => axios.delete(`${API}/pdi/${id}`),
  updateAcao: (pdiId, acaoId, data) => axios.put(`${API}/pdi/${pdiId}/acao/${acaoId}`, data),
};

// PDC (Plano de Desenvolvimento Coletivo)
export const pdcAPI = {
  getFiltros: () => axios.get(`${API}/pdc/filtros`),
  previewParticipantes: (data) => axios.post(`${API}/pdc/preview`, data),
  create: (data) => axios.post(`${API}/pdc`, data),
  getAll: (params) => axios.get(`${API}/pdc`, { params }),
  getById: (id) => axios.get(`${API}/pdc/${id}`),
  update: (id, data) => axios.put(`${API}/pdc/${id}`, data),
  delete: (id) => axios.delete(`${API}/pdc/${id}`),
  gerarPDIs: (id) => axios.post(`${API}/pdc/${id}/gerar-pdis`),
};

// Employee CSV Import
export const employeeImportAPI = {
  downloadTemplate: () => axios.get(`${API}/employees/csv-template`, { responseType: 'blob' }),
  importCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/employees/import-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Company Features (for access control)
export const featuresAPI = {
  getMyFeatures: () => axios.get(`${API}/company/features`),
};

// Company Users Management (Multi-User System)
export const companyUsersAPI = {
  getAll: () => axios.get(`${API}/companies/users`),
  add: (data) => axios.post(`${API}/companies/users`, data),
  update: (userId, data) => axios.put(`${API}/companies/users/${userId}`, data),
  remove: (userId) => axios.delete(`${API}/companies/users/${userId}`),
  getLimit: () => axios.get(`${API}/companies/users/limit`),
};

// Constantes de perfis e permissões
export const PERFIS_USUARIO = {
  proprietario: { nome: 'Proprietário', descricao: 'Acesso total e gerenciamento de usuários' },
  administrador: { nome: 'Administrador', descricao: 'Editar tudo, visualizar configurações' },
  operador: { nome: 'Operador', descricao: 'Operações diárias (funcionários, vagas, etc)' },
  visualizador: { nome: 'Visualizador', descricao: 'Apenas consulta, sem edição' }
};

export const PERMISSOES_MODULOS = {
  funcionarios: 'Funcionários',
  vagas: 'Vagas',
  avaliacoes: 'Avaliações',
  testes: 'Testes',
  configuracoes: 'Configurações'
};

export const NIVEIS_PERMISSAO = {
  editar: 'Editar',
  visualizar: 'Visualizar',
  nenhum: 'Nenhum'
};

// Company Billing API
export const companyBillingAPI = {
  getBilling: () => axios.get(`${API}/company/billing`),
  getInvoices: () => axios.get(`${API}/company/billing/invoices`),
};

// Turmas de Treinamento API
export const turmasAPI = {
  create: (data) => axios.post(`${API}/turmas`, data),
  getAll: (params) => axios.get(`${API}/turmas`, { params }),
  getById: (id) => axios.get(`${API}/turmas/${id}`),
  update: (id, data) => axios.put(`${API}/turmas/${id}`, data),
  addParticipante: (turmaId, data) => axios.post(`${API}/turmas/${turmaId}/participantes`, data),
  updateParticipante: (turmaId, employeeId, data) => axios.put(`${API}/turmas/${turmaId}/participantes/${employeeId}`, data),
  removeParticipante: (turmaId, employeeId) => axios.delete(`${API}/turmas/${turmaId}/participantes/${employeeId}`),
  concluir: (turmaId, data) => axios.post(`${API}/turmas/${turmaId}/concluir`, data),
  autoGenerate: (trainingIds) => axios.post(`${API}/turmas/auto-generate`, trainingIds),
  getListaPresenca: (turmaId) => axios.get(`${API}/turmas/${turmaId}/lista-presenca`),
};

// LNT - Levantamento de Necessidade de Treinamento
export const lntAPI = {
  get: (params) => axios.get(`${API}/lnt`, { params }),
  getConfig: () => axios.get(`${API}/lnt/config`),
  updateConfig: (data) => axios.put(`${API}/lnt/config`, data),
  criarTurmaRenovacao: (data) => axios.post(`${API}/lnt/criar-turma-renovacao`, data),
};

// Storage API (uploads gerais)
export const financeAPI = {
  get: () => axios.get(`${API}/me/finance`),
  addTransaction: (data) => axios.post(`${API}/me/finance/transactions`, data),
  deleteTransaction: (id) => axios.delete(`${API}/me/finance/transactions/${id}`),
  saveChallenge: (plan) => axios.put(`${API}/me/finance/challenge`, { plan }),
};

export const onboardingAPI = {
  getStatus: (tour) => axios.get(`${API}/users/me/onboarding/${tour}`),
  complete: (tour) => axios.post(`${API}/users/me/onboarding`, { tour }),
};

export const storageAPI = {
  upload: (formData) => axios.post(`${API}/news-feed/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Partners API (PAT/Prefeituras)
export const partnerPortalAPI = {
  candidates: (q) => axios.get(`${API}/partner-portal/candidates`, { params: { q } }),
  createCandidate: (data) => axios.post(`${API}/partner-portal/candidates`, data),
  jobs: (scope) => axios.get(`${API}/partner-portal/jobs`, { params: { scope } }),
  refer: (job_id, candidate_ids) => axios.post(`${API}/partner-portal/referrals`, { job_id, candidate_ids }),
  referrals: () => axios.get(`${API}/partner-portal/referrals`),
  report: () => axios.get(`${API}/partner-portal/report`),
  invite: () => axios.get(`${API}/partner-portal/invite`),
  byInvite: (code) => axios.get(`${API}/partners/by-invite/${code}`),
};

export const partnersAPI = {
  getPublicList: () => axios.get(`${API}/partners/public`),
  getMe: () => axios.get(`${API}/partners/me`),
  getAll: (params) => axios.get(`${API}/partners`, { params }),
  getById: (id) => axios.get(`${API}/partners/${id}`),
  create: (data) => axios.post(`${API}/partners`, data),
  update: (id, data) => axios.put(`${API}/partners/${id}`, data),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/partners/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Cartas de Encaminhamento API
export const cartasAPI = {
  create: (data) => axios.post(`${API}/cartas-encaminhamento`, data),
  getAll: (params) => axios.get(`${API}/cartas-encaminhamento`, { params }),
  getById: (id) => axios.get(`${API}/cartas-encaminhamento/${id}`),
  updateRetorno: (id, data) => axios.put(`${API}/cartas-encaminhamento/${id}/retorno`, data),
};

// Climate Survey API (Pesquisa de Clima Organizacional)
export const climateSurveyAPI = {
  // Empresa
  list: (params) => axios.get(`${API}/climate-surveys`, { params }),
  getById: (id) => axios.get(`${API}/climate-surveys/${id}`),
  create: (data) => axios.post(`${API}/climate-surveys`, data),
  update: (id, data) => axios.put(`${API}/climate-surveys/${id}`, data),
  delete: (id) => axios.delete(`${API}/climate-surveys/${id}`),
  getResults: (id) => axios.get(`${API}/climate-surveys/${id}/results`),
  getReportData: (id) => axios.get(`${API}/climate-surveys/${id}/report-data`),
  getAvailableComparisons: (id) => axios.get(`${API}/climate-surveys/${id}/available-comparisons`),
  getComparison: (id, compareIds) => axios.get(`${API}/climate-surveys/${id}/comparison`, { params: { compare_ids: compareIds.join(',') } }),
  
  // Banco de Perguntas
  questionBank: {
    list: (params) => axios.get(`${API}/climate-question-bank`, { params }),
    create: (data) => axios.post(`${API}/climate-question-bank`, data),
    delete: (id) => axios.delete(`${API}/climate-question-bank/${id}`),
    getCategories: () => axios.get(`${API}/climate-question-bank/categories`),
    downloadTemplate: () => axios.get(`${API}/climate-question-bank/template`, { responseType: 'blob' }),
    importCSV: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/climate-question-bank/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
  },
  
  // Funcionário (Employee)
  employeeGetPending: () => axios.get(`${API}/employee/climate-surveys`),
  employeeGetSurvey: (id) => axios.get(`${API}/employee/climate-surveys/${id}`),
  employeeSubmit: (id, data) => axios.post(`${API}/employee/climate-surveys/${id}/respond`, data),
};

// NR01 Diagnostic API (Diagnóstico Psicossocial)
export const nr01API = {
  // Dimensões e perguntas padrão
  getDimensoes: () => axios.get(`${API}/nr01/dimensoes`),
  
  // Empresa
  list: (params) => axios.get(`${API}/nr01/diagnosticos`, { params }),
  getById: (id) => axios.get(`${API}/nr01/diagnosticos/${id}`),
  create: (data) => axios.post(`${API}/nr01/diagnosticos`, data),
  update: (id, data) => axios.put(`${API}/nr01/diagnosticos/${id}`, data),
  delete: (id) => axios.delete(`${API}/nr01/diagnosticos/${id}`),
  getResults: (id) => axios.get(`${API}/nr01/diagnosticos/${id}/resultados`),
  generateRecommendations: (id) => axios.post(`${API}/nr01/diagnosticos/${id}/recomendacoes`),
  exportPGR: (id) => axios.get(`${API}/nr01/diagnosticos/${id}/exportar-pgr`),
  
  // Evolução histórica por CNPJ
  getEvolution: () => axios.get(`${API}/nr01/evolucao`),
  
  // Área do Psicólogo
  getPsychologistSectors: (id) => axios.get(`${API}/nr01/diagnosticos/${id}/psicologo/setores`),
  getPsychologistResponses: (id, setor) => axios.get(`${API}/nr01/diagnosticos/${id}/psicologo/respostas/${encodeURIComponent(setor)}`),
  getPsychologistInterviews: (id, setor) => axios.get(`${API}/nr01/diagnosticos/${id}/psicologo/entrevistas/${encodeURIComponent(setor)}`),
  savePsychologistInterview: (id, data) => axios.post(`${API}/nr01/diagnosticos/${id}/psicologo/entrevista`, data),
  savePsychologistAnalysis: (id, data) => axios.post(`${API}/nr01/diagnosticos/${id}/psicologo/analise`, data),
  getPsychologistFullReport: (id) => axios.get(`${API}/nr01/diagnosticos/${id}/psicologo/relatorio-completo`),
  
  // Funcionário (Employee)
  employeeGetPending: () => axios.get(`${API}/employee/nr01/diagnosticos`),
  employeeGetDiagnostic: (id) => axios.get(`${API}/employee/nr01/diagnosticos/${id}`),
  employeeSubmit: (id, data) => axios.post(`${API}/employee/nr01/diagnosticos/${id}/responder`, data),
};

// Test Invites (Convites de Testes Comportamentais)
export const testInvitesAPI = {
  create: (data) => axios.post(`${API}/test-invites`, data),
  list: (params) => axios.get(`${API}/test-invites`, { params }),
  getById: (id) => axios.get(`${API}/test-invites/${id}`),
  delete: (id) => axios.delete(`${API}/test-invites/${id}`),
  validate: (token) => axios.get(`${API}/test-invites/validate/${token}`),
  complete: (token) => axios.post(`${API}/test-invites/complete/${token}`),
  saveResult: (token, data) => axios.post(`${API}/test-invites/${token}/save-result`, data),
};

// Psychologists (Psicólogos da Empresa)
export const psychologistsAPI = {
  create: (data) => axios.post(`${API}/psychologists`, data),
  list: () => axios.get(`${API}/psychologists`),
  delete: (id) => axios.delete(`${API}/psychologists/${id}`),
};

// Pending Tests for Mobile App
export const pendingTestsAPI = {
  getMyPending: () => axios.get(`${API}/test-invites/pending-for-me`),
  checkExisting: (cpf, tipoTeste) => axios.get(`${API}/test-invites/check-existing`, { params: { cpf, tipo_teste: tipoTeste } }),
  useExisting: (data) => axios.post(`${API}/test-invites/use-existing`, null, { params: data }),
};

// Test Groups (Grupos de Testes Comportamentais)
export const testGroupsAPI = {
  create: (data) => axios.post(`${API}/test-groups`, data),
  list: () => axios.get(`${API}/test-groups`),
  getById: (id) => axios.get(`${API}/test-groups/${id}`),
  delete: (id) => axios.delete(`${API}/test-groups/${id}`),
  // Gerenciar membros
  addMember: (groupId, member) => axios.post(`${API}/test-groups/${groupId}/members`, member),
  updateMember: (groupId, cpf, member) => axios.put(`${API}/test-groups/${groupId}/members/${cpf}`, member),
  removeMember: (groupId, cpf) => axios.delete(`${API}/test-groups/${groupId}/members/${cpf}`),
};

// Trainings (Treinamentos Obrigatórios)
export const trainingsAPI = {
  reclassificationPreview: () => axios.get(`${API}/trainings/reclassification/preview`),
  reclassificationApply: (training_ids) => axios.post(`${API}/trainings/reclassification/apply`, { training_ids }),
  create: (data) => axios.post(`${API}/trainings`, data),
  list: (params) => axios.get(`${API}/trainings`, { params }),
  getCategories: () => axios.get(`${API}/trainings/categories`),
  getById: (id) => axios.get(`${API}/trainings/${id}`),
  update: (id, data) => axios.put(`${API}/trainings/${id}`, data),
  delete: (id) => axios.delete(`${API}/trainings/${id}`),
  getExpiring: (id, days = 30) => axios.get(`${API}/trainings/${id}/expiring`, { params: { days } }),
};

// Employee Trainings (Treinamentos de Funcionários)
export const employeeTrainingsAPI = {
  create: (data) => axios.post(`${API}/employee-trainings`, data),
  list: (params) => axios.get(`${API}/employee-trainings`, { params }),
  getEmployeeStatus: (employeeId) => axios.get(`${API}/employees/${employeeId}/training-status`),
};

// Employee Activities (Gestão de Atividades)
export const employeeActivitiesAPI = {
  create: (data) => axios.post(`${API}/employee-activities`, data),
  list: (params) => axios.get(`${API}/employee-activities`, { params }),
  getById: (id) => axios.get(`${API}/employee-activities/${id}`),
  update: (id, data) => axios.put(`${API}/employee-activities/${id}`, data),
  delete: (id) => axios.delete(`${API}/employee-activities/${id}`),
  prorrogar: (id, data) => axios.post(`${API}/employee-activities/${id}/prorrogar`, data),
  encerrar: (id, data) => axios.post(`${API}/employee-activities/${id}/encerrar`, data),
  compareRequirements: (employeeId, cargoId) => axios.get(`${API}/employees/${employeeId}/compare-requirements/${cargoId}`),
  // Histórico de cargos
  getHistoricoCargos: (employeeId) => axios.get(`${API}/employees/${employeeId}/historico-cargos`),
  getHistoricoCargosForPdf: (employeeId) => axios.get(`${API}/employees/${employeeId}/historico-cargos/pdf`),
};

// Estágio Probatório
export const probationPeriodsAPI = {
  create: (data) => axios.post(`${API}/probation-periods`, data),
  list: (params) => axios.get(`${API}/probation-periods`, { params }),
  getById: (id) => axios.get(`${API}/probation-periods/${id}`),
  delete: (id) => axios.delete(`${API}/probation-periods/${id}`),
  addObjetivo: (id, data) => axios.post(`${API}/probation-periods/${id}/objetivos`, data),
  updateObjetivo: (id, objetivoId, data) => axios.put(`${API}/probation-periods/${id}/objetivos/${objetivoId}`, data),
  addAvaliacao: (id, data) => axios.post(`${API}/probation-periods/${id}/avaliacoes`, data),
  vincularPerformance: (id, data) => axios.post(`${API}/probation-periods/${id}/avaliacoes/vincular-performance`, data),
  decisaoFinal: (id, data) => axios.post(`${API}/probation-periods/${id}/decisao`, data),
  getAditivoPdf: (id) => axios.get(`${API}/probation-periods/${id}/aditivo/pdf`),
  marcarAssinado: (id) => axios.post(`${API}/probation-periods/${id}/aditivo/assinar`),
  // Alertas e notificações
  getAlertas: () => axios.get(`${API}/probation-periods/alertas`),
  enviarNotificacoes: () => axios.post(`${API}/probation-periods/enviar-notificacoes`),
};

// Dashboard Alertas
export const dashboardAlertasAPI = {
  getAlertas: () => axios.get(`${API}/company/dashboard/alertas`),
};

// Planos e Limites de Empresas Gerenciadas
export const companyPlanAPI = {
  getLimites: () => axios.get(`${API}/companies/plano/limites`),
  getUpgradeOptions: () => axios.get(`${API}/companies/plano/upgrade-options`),
  validarAdicaoEmpresa: () => axios.post(`${API}/companies/managed/validar`),
};

// Skills Matrix (Matriz de Polivalência)
export const skillsMatrixAPI = {
  getMatrix: (setor) => axios.get(`${API}/company/skills-matrix`, { params: { setor } }),
  getEmployeeDetail: (employeeId) => axios.get(`${API}/company/skills-matrix/employee/${employeeId}`),
  getGaps: (employeeId, cargoId) => axios.get(`${API}/company/skills-matrix/gaps/${employeeId}/${cargoId}`),
  generatePDI: (employeeId, cargoId, periodoMeses = 6) => 
    axios.post(`${API}/company/skills-matrix/generate-pdi`, null, { 
      params: { employee_id: employeeId, cargo_id: cargoId, periodo_meses: periodoMeses } 
    }),
  getGapsDashboard: () => axios.get(`${API}/company/gaps-dashboard`),
};

// Cargo Matching (Vínculo Cargo-Descrição)
export const cargoMatchingAPI = {
  analyze: () => axios.get(`${API}/company/cargo-matching/analysis`),
  vincular: (cargoFuncionario, descricaoId) => 
    axios.post(`${API}/company/cargo-matching/vincular`, null, {
      params: { cargo_funcionario: cargoFuncionario, descricao_id: descricaoId }
    }),
};

// Training Grids (Grades de Treinamento por Setor)
export const trainingGridsAPI = {
  create: (data) => axios.post(`${API}/training-grids`, data),
  getAll: (params) => axios.get(`${API}/training-grids`, { params }),
  list: (params) => axios.get(`${API}/training-grids`, { params }),
  getById: (id) => axios.get(`${API}/training-grids/${id}`),
  update: (id, data) => axios.put(`${API}/training-grids/${id}`, data),
  delete: (id) => axios.delete(`${API}/training-grids/${id}`),
  getBySetorSummary: () => axios.get(`${API}/training-grids/by-setor/summary`),
};

// Job Positions AI & Import
export const jobPositionsImportAPI = {
  importDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/job-positions/import-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  generateWithAI: (titulo, codigoCbo, setor, nivelHierarquico, descricaoPrevia) => 
    axios.post(`${API}/job-positions/generate-ai`, null, { 
      params: { titulo, codigo_cbo: codigoCbo, setor, nivel_hierarquico: nivelHierarquico, descricao_previa: descricaoPrevia } 
    }),
};

// Feed de Notícias / Comunicados (Company)
export const newsFeedAPI = {
  // Company endpoints
  getAll: (params) => axios.get(`${API}/news-feed`, { params }),
  getOne: (id) => axios.get(`${API}/news-feed/${id}`),
  create: (data) => axios.post(`${API}/news-feed`, data),
  update: (id, data) => axios.put(`${API}/news-feed/${id}`, data),
  delete: (id) => axios.delete(`${API}/news-feed/${id}`),
  getReads: (id) => axios.get(`${API}/news-feed/${id}/reads`),
  getReactions: (id) => axios.get(`${API}/news-feed/${id}/reactions`),
  react: (id, tipo) => axios.post(`${API}/employee/news-feed/${id}/react`, { tipo }),
  reactCandidate: (id, tipo) => axios.post(`${API}/candidate/news-feed/${id}/react`, { tipo }),
  getEngagementReport: (id) => axios.get(`${API}/news-feed/${id}/engagement-report`),
  getEngagementStats: () => axios.get(`${API}/news-feed/engagement-stats`),
  // Employee endpoints (mobile)
  getEmployeeFeed: () => axios.get(`${API}/employee/news-feed`),
  getEmployeeDetail: (id) => axios.get(`${API}/employee/news-feed/${id}`),
  markAsRead: (id) => axios.post(`${API}/employee/news-feed/${id}/read`),
  // Candidate endpoints (mobile)
  getCandidateFeed: () => axios.get(`${API}/candidate/news-feed`),
  getCandidateDetail: (id) => axios.get(`${API}/candidate/news-feed/${id}`),
  markCandidateAsRead: (id) => axios.post(`${API}/candidate/news-feed/${id}/read`),
};

// Treinamentos Online (Employee Mobile)
export const treinamentosOnlineAPI = {
  // Listar treinamentos do funcionário
  getAll: () => axios.get(`${API}/employee/treinamentos`),
  // Detalhe de um treinamento (com vídeo e questões)
  getById: (turmaId) => axios.get(`${API}/employee/treinamentos/${turmaId}`),
  // Atualizar progresso de visualização do vídeo
  updateProgresso: (turmaId, percentual) => axios.post(`${API}/employee/treinamentos/${turmaId}/progresso`, { percentual_assistido: percentual }),
  // Submeter respostas da prova
  submitProva: (turmaId, respostas) => axios.post(`${API}/employee/treinamentos/${turmaId}/prova`, { respostas }),
  // Obter resultado da prova
  getResultado: (turmaId) => axios.get(`${API}/employee/treinamentos/${turmaId}/resultado`),
};

// Descrições de Cargos
export const jobDescriptionsAPI = {
  getAll: () => axios.get(`${API}/companies/job-descriptions`),
  getById: (id) => axios.get(`${API}/companies/job-descriptions/${id}`),
  create: (data) => axios.post(`${API}/companies/job-descriptions`, data),
  update: (id, data) => axios.put(`${API}/companies/job-descriptions/${id}`, data),
  delete: (id) => axios.delete(`${API}/companies/job-descriptions/${id}`),
};

// Solicitações de Mudanças (Change Requests)
export const changeRequestsAPI = {
  // CRUD básico
  create: (data) => axios.post(`${API}/change-requests`, data),
  list: (params) => axios.get(`${API}/change-requests`, { params }),
  getById: (id) => axios.get(`${API}/change-requests/${id}`),
  update: (id, data) => axios.put(`${API}/change-requests/${id}`, data),
  delete: (id) => axios.delete(`${API}/change-requests/${id}`),
  
  // Ações de workflow
  enviar: (id) => axios.post(`${API}/change-requests/${id}/enviar`),
  aprovar: (id, data) => axios.post(`${API}/change-requests/${id}/aprovar`, data),
  rejeitar: (id, data) => axios.post(`${API}/change-requests/${id}/rejeitar`, data),
  cancelar: (id, data) => axios.post(`${API}/change-requests/${id}/cancelar`, data),
  efetivar: (id) => axios.post(`${API}/change-requests/${id}/efetivar`),
  
  // Contagem de pendentes (para notificação)
  getPendingCount: () => axios.get(`${API}/change-requests/pending-count`),
};
